import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { classes } from '../mock';
import { ArrowUpRight } from 'lucide-react';
import { RevealHeading } from './anim/SectionReveal';
import { useEnrolmentModal } from '../context/EnrolmentModalContext';

const ClassCard = ({ c, i, onEnrol }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const imgY = useTransform(scrollYProgress, [0, 1], ['-10%', '10%']);

  return (
    <motion.article
      ref={ref}
      style={{ y }}
      initial={{ opacity: 0, y: 80, scale: 0.95, filter: 'blur(6px)' }}
      whileInView={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: (i % 3) * 0.12, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="class-card group bg-white overflow-hidden border border-[#eadfc9] flex flex-col"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <motion.img
          style={{ y: imgY }}
          src={c.img}
          alt={c.title}
          className="absolute inset-0 w-full h-[120%] object-cover transition-transform duration-[900ms] group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        <div className="absolute top-4 left-4 bg-white/95 backdrop-blur px-3 py-1 text-[10px] font-sans-pro uppercase tracking-[0.28em] text-[#8f6417] border-l-2 border-[#c8942a]">
          {c.age}
        </div>
      </div>
      <div className="p-7 flex-1 flex flex-col">
        <h3 className="font-serif-display text-2xl font-black text-[#1c1a17]">{c.title}</h3>
        <div className="h-px w-10 bg-[#c8942a] my-4" />
        <p className="text-[#4a4238] font-light flex-1 leading-relaxed text-[14px]">{c.desc}</p>
        <a href="#contact" onClick={(e) => { e.preventDefault(); onEnrol(c.title); }} className="mt-6 inline-flex items-center gap-2 font-sans-pro text-[11px] uppercase tracking-[0.28em] text-[#1c1a17] hover:text-[#b8331f] transition-colors">
          Explore programme <ArrowUpRight size={14} />
        </a>
      </div>
    </motion.article>
  );
};

const Classes = () => {
  const { show } = useEnrolmentModal();
  const openEnrol = (programmeTitle) => {
    const mapped =
      programmeTitle === 'Foundation / A-Level' ? 'Foundation / A-Level' : programmeTitle;
    show({ programme: mapped });
  };
  return (
    <section id="classes" className="py-24 md:py-32 bg-[#fbf7f2] border-t border-[#eadfc9]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16"
        >
          <div className="max-w-2xl">
            <div className="tag-label">Academic Programmes</div>
            <RevealHeading
              text="A pathway for every stage of learning"
              highlight="every"
              className="font-serif-display text-4xl md:text-6xl font-black mt-4 text-[#1c1a17] leading-[1.05]"
            />
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="text-[#4a4238] max-w-md text-[15px] font-light leading-relaxed"
          >
            From Montessori-inspired early years to the internationally recognised Cambridge A-Levels,
            our programmes are calibrated for depth, breadth and personal excellence.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {classes.map((c, i) => (
            <ClassCard key={c.title} c={c} i={i} onEnrol={openEnrol} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Classes;
